import { z } from 'zod';
import {
  FleetEngineNameSchema,
  FleetLaneNameSchema,
  FleetModelIdSchema,
  FleetProjectNameSchema,
} from './fleet-status.schemas';

/**
 * The relay command envelope (IMM-910.3, D9) — NOT an HTTP contract. Shared so
 * the fleet agent and the backend's dispatcher parse through one definition,
 * the same way `fleet-status.schemas.ts` shares the status event with its
 * projection.
 *
 * From IMM-910.5, every verb executes for real — `judgeCommand` no longer
 * refuses every well-formed command, and the outcome vocabulary below is what
 * the four executors actually report. IMM-910.6 adds the three log verbs —
 * the first verbs that carry a parameter beyond `profile` — so both the body
 * and the envelope become discriminated unions on `verb`: a flat object with
 * optional fields would let `stop` be sent with a `stream`.
 *
 * The union has since gained a second axis: whether a verb names a lane at
 * all. `sync-branch`, `clean-checkout`, `start-all` and `stop-all` act on the
 * host as a whole and carry NO profile — see `FLEET_HOST_COMMAND_VERBS`.
 *
 * fleet-agent ADR 0004 took the per-lane `start`, `stop` and `finish` away;
 * ADR 0013 brings them back as `start`, `stop` (with `finish`: after the round
 * in hand) and `restart`, each naming a lane (`profile`) and optionally the one
 * `project` it is narrowed to. The host keeps the stop, so a stopped lane stays
 * stopped across a reboot, an update and a daemon restart — see
 * `FLEET_LANE_COMMAND_VERBS`. The assisted lane's `prompt` stays gone.
 * `claude-account` retired with IMM-1450: accounts rotate on the worker (fleet-agent ADR 0027).
 */
export const FLEET_COMMAND_VERBS = [
  'start',
  'stop',
  'restart',
  'sync-branch',
  'clean-checkout',
  'restart-agent',
  'update-agent',
  'force-update-agent',
  'start-all',
  'stop-all',
  'restart-all',
  'logs',
  'logs-download',
  'logs-reset',
  'handover',
  'engine',
  'steer',
  'e2e-unlock',
  'run-command',
  'system-preview',
  'system-prune',
  'agent',
] as const;

/**
 * `e2e-unlock` (fleet-agent 0.22.0, IMM-1153): break the lock on one e2e stack of the host, in each project it
 * serves (or the one named), through the project's `stack unlock`. `worker` names whose stack; absent, the host's
 * manual stack — the shared one a dead round left locked on lukas-agent. For an operator who cannot reach the host:
 * the outcome says who held the lock and since when. Host-wide, no profile.
 */
export const FLEET_WORKER_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;

/** A worker id, as `e2e-unlock` names whose stack: `mini-pc-engineer-1`, `manual-mini-pc`. */
export const FleetWorkerSchema = z.string().regex(FLEET_WORKER_PATTERN);

/**
 * `run-command` (fleet-agent 0.23.0, its ADR 0016): one pnpm command an operator runs in one project's checkout on
 * another host, for a fix the fleet has no verb for yet (`pnpm --filter @immo/end-to-end e2e:unlock`). Only pnpm,
 * never through a shell: the host hands the words to the binary as they are, so `;`, `&&` or a pipe cannot chain a
 * second program on, and pnpm's own ways to run any binary are refused. A guard, not a sandbox — a project's scripts
 * still run whatever they say. Host-wide, no profile; `project` always named.
 */
export const RUN_COMMAND_BINARY = 'pnpm';
export const RUN_COMMAND_MAX_WORDS = 64;
export const RUN_COMMAND_MAX_WORD_LENGTH = 1000;
/** The tail of a command's output the outcome carries back. */
export const RUN_COMMAND_OUTPUT_MAX_CHARS = 16_000;

/** pnpm's ways to run an arbitrary binary, and the shell metacharacters a script's extra args could carry. */
const RUN_COMMAND_REFUSED_WORDS: ReadonlySet<string> = new Set(['exec', 'dlx', 'create', '-c', '--shell-mode']);
const RUN_COMMAND_SHELL_CHARACTERS = /[;&|<>`$\n\r\\]/;

/** Why `argv` may not run on another host, or `null` when it may. The same check the host runs before it runs them. */
export function runCommandArgvError(argv: readonly string[]): string | null {
  const [binary, ...rest] = argv;

  if (binary === undefined) return 'the command is empty';

  if (binary !== RUN_COMMAND_BINARY) return `only ${RUN_COMMAND_BINARY} commands run on another host, not ${binary}`;

  if (rest.length === 0) return `${RUN_COMMAND_BINARY} needs a command`;

  if (argv.length > RUN_COMMAND_MAX_WORDS) return `the command has more than ${RUN_COMMAND_MAX_WORDS} words`;

  for (const word of rest) {
    if (word.length > RUN_COMMAND_MAX_WORD_LENGTH)
      return `a word is longer than ${RUN_COMMAND_MAX_WORD_LENGTH} characters`;

    if (RUN_COMMAND_REFUSED_WORDS.has(word))
      return `${RUN_COMMAND_BINARY} ${word} runs any binary — run a package script instead`;

    if (RUN_COMMAND_SHELL_CHARACTERS.test(word))
      return `"${word}" has a shell character — the command runs without a shell, one at a time`;
  }

  return null;
}

/** The words a `run-command` runs: pnpm first, no shell character, none of pnpm's ways to run any binary. */
export const FleetRunCommandArgvSchema = z
  .array(z.string().min(1).max(RUN_COMMAND_MAX_WORD_LENGTH))
  .min(2)
  .max(RUN_COMMAND_MAX_WORDS)
  .superRefine((argv, ctx) => {
    const error = runCommandArgvError(argv);

    if (error !== null) ctx.addIssue({ code: 'custom', message: error });
  });

/**
 * `system-preview` / `system-prune` (fleet-agent 0.24.0, its ADR 0018): the host's disk report, or its prune — stale
 * worktrees, leaked testcontainers, old Docker images and build cache, the pnpm store. Host-wide, no profile;
 * `project` only narrows the worktree pile (absent: every project the host serves). The outcome carries the report.
 */
export const FLEET_SYSTEM_COMMAND_VERBS = ['system-preview', 'system-prune'] as const;
export type FleetSystemCommandVerb = (typeof FLEET_SYSTEM_COMMAND_VERBS)[number];
/** The tail of the report the outcome carries back. */
export const SYSTEM_OUTPUT_MAX_CHARS = 16_000;

/**
 * `agent` (ADR 0070): one verb that carries any verb fleet-agent defines on its own. The backend stores and forwards
 * `{ action, args }` without interpreting it; the host runs it only when its own table names the action and the args
 * pass that action's check, and settles `refused` otherwise. Host-wide, no profile. An action never passes an arg to
 * a shell, never names a file path to write and never takes a secret — the host enforces that, not this contract.
 */
export const FLEET_AGENT_ACTION_PATTERN = /^[a-z][a-z0-9-]{0,31}$/;
export const FLEET_AGENT_ARG_KEY_PATTERN = /^[a-z][a-z0-9-]{0,31}$/;
export const FLEET_AGENT_MAX_ARGS = 16;
export const FLEET_AGENT_ARG_MAX_LENGTH = 500;
/** The tail of the output the outcome carries back. */
export const AGENT_OUTPUT_MAX_CHARS = 16_000;

export const FleetAgentActionSchema = z.string().regex(FLEET_AGENT_ACTION_PATTERN);

/** Flat: string (<= 500) | finite number | boolean | null per key; no nesting, no arrays. */
export const FleetAgentArgsSchema = z
  .record(
    z.string().regex(FLEET_AGENT_ARG_KEY_PATTERN),
    z.union([z.string().max(FLEET_AGENT_ARG_MAX_LENGTH), z.number().finite(), z.boolean(), z.null()]),
  )
  .refine((args) => Object.keys(args).length <= FLEET_AGENT_MAX_ARGS, {
    message: `at most ${FLEET_AGENT_MAX_ARGS} args`,
  });
export type FleetAgentArgs = z.infer<typeof FleetAgentArgsSchema>;

/**
 * `steer` (OPS-55, fleet-agent ADR 0004 B2): a person's message to one live JOB, from the fleet page — never sent
 * through the operator's command body. It names the job and the worker holding it, not a lane; the host appends it
 * to that worker's steer inbox, which the run reads at its next tool call. A bare `stop` (see
 * {@link isFleetSteerStop}) asks the run to end at its next safe point; any other text is an instruction. The host
 * settles it `ok` once it is in the inbox, `refused` when the worker holds no round on this host.
 */
export const FLEET_JOB_MESSAGE_MAX_LENGTH = 4000;

/**
 * `stop`, and nothing that merely contains it — the rule Buzz steering used (fleet-agent `isStop`). "don't stop
 * until the tests pass" is an instruction; a false stop destroys a run's remaining work, so only the trimmed,
 * case-insensitive whole message counts.
 */
export function isFleetSteerStop(text: string): boolean {
  return text.trim().toLowerCase() === 'stop';
}

/**
 * `handover` (OPS-29): the host bundles one TICKET's worktrees to origin and
 * removes them, so another host's takeover can load the work. It names a
 * ticket rather than a lane — the worktrees outlive the round that made them —
 * and carries no profile.
 */
export const FLEET_TICKET_PATTERN = /^[A-Z]{1,4}-\d+$/;
const FleetTicketSchema = z.string().regex(FLEET_TICKET_PATTERN);

/**
 * `engine` (fleet-agent 0.19.0, its ADR 0004): the host's `fleet-agent engine set|unset` from afar — which engine
 * and models the host's lanes run on from each lane's next round. No `lane`: the host default; a `lane`: that
 * lane's override, narrowed to one project by `project`. `engine: null` unsets it. Host-wide, no profile: an
 * override names a lane the way a project declares it, across every project the host serves. The host settles it
 * `ok`, or `refused` with the reason in `detail` (lane not declared, CLI not installed, a plugin-skill lane moved
 * to an engine that cannot load it).
 */

/**
 * The verbs that act on the WHOLE host and carry no profile.
 *
 * `sync-branch` and `clean-checkout` both run `git` in the host's primary
 * checkout — the one every lane shares. `restart-agent` restarts the
 * fleet-agent's own service unit, and `update-agent` (OPS-58) pulls the
 * agent's own clone, re-installs and restarts it — `fleet-agent update` from
 * afar; `force-update-agent` (OPS-59) first resets that clone to its upstream,
 * discarding local changes and commits, and re-installs even when nothing
 * moved — `fleet-agent update --force`. `start-all`, `stop-all` and
 * `restart-all` start, stop and cycle the host's lanes — every lane of every
 * project it serves, whatever project the body names. They carry no
 * profile at all rather than a decorative one, which is what lets the page
 * render them once per machine, and what keeps the activity feed from
 * attributing a host-wide act to whichever lane the operator was looking at.
 */
export const FLEET_HOST_COMMAND_VERBS = [
  'sync-branch',
  'clean-checkout',
  'restart-agent',
  'update-agent',
  'force-update-agent',
  'start-all',
  'stop-all',
  'restart-all',
] as const;

/**
 * The verbs that act on ONE lane of the host (fleet-agent ADR 0013): `profile` names it, `project` narrows it to
 * one project (absent: every project the host serves that declares it). `stop` may say `finish`: after the round in
 * hand rather than at the next step boundary.
 */
export const FLEET_LANE_COMMAND_VERBS = ['start', 'stop', 'restart'] as const;
export type FleetLaneCommandVerb = (typeof FLEET_LANE_COMMAND_VERBS)[number];

export const FleetCommandVerbSchema = z.enum(FLEET_COMMAND_VERBS);
export type FleetCommandVerb = (typeof FLEET_COMMAND_VERBS)[number];
export type FleetHostCommandVerb = (typeof FLEET_HOST_COMMAND_VERBS)[number];

/** The ONE membership test every layer consumes — the DB CHECK, the judge, the page and the mappers. */
export function isFleetHostCommandVerb(verb: FleetCommandVerb): verb is FleetHostCommandVerb {
  return (FLEET_HOST_COMMAND_VERBS as readonly FleetCommandVerb[]).includes(verb);
}

export function isFleetSystemCommandVerb(verb: FleetCommandVerb): verb is FleetSystemCommandVerb {
  return (FLEET_SYSTEM_COMMAND_VERBS as readonly FleetCommandVerb[]).includes(verb);
}

export function isFleetLaneCommandVerb(verb: FleetCommandVerb): verb is FleetLaneCommandVerb {
  return (FLEET_LANE_COMMAND_VERBS as readonly FleetCommandVerb[]).includes(verb);
}

/**
 * The host verbs that command every LANE at once, as opposed to the ones that
 * act on the checkout or the agent process.
 *
 * They are mutually exclusive on the host's own reported state — a machine
 * with a lane running offers `stop-all`, one with none offers `start-all` —
 * so the page needs to tell these apart from the checkout and agent verbs
 * rather than rendering them all in a row. `restart-all` (ADR 0013) is offered
 * beside whichever of the pair applies.
 */
export const FLEET_ALL_LANES_COMMAND_VERBS = ['start-all', 'stop-all', 'restart-all'] as const;
export type FleetAllLanesCommandVerb = (typeof FLEET_ALL_LANES_COMMAND_VERBS)[number];

export function isFleetAllLanesCommandVerb(verb: FleetCommandVerb): verb is FleetAllLanesCommandVerb {
  return (FLEET_ALL_LANES_COMMAND_VERBS as readonly FleetCommandVerb[]).includes(verb);
}

/**
 * Who issued a command (IMM-939).
 *
 * A command used to have exactly one possible issuer — an operator holding
 * `backoffice:fleet:command` — so the row recorded an admin id and nothing
 * else. `fleet-agent agent fleet` adds a second: an approved MACHINE, signing with
 * the fleet identity its host already owns. The row stays the only witness to
 * who issued a privileged act, so it records the KIND beside the id rather
 * than guessing from which of two nullable columns happens to be set.
 */
export const FLEET_COMMAND_ISSUER_KINDS = ['admin', 'machine'] as const;
export const FleetCommandIssuerKindSchema = z.enum(FLEET_COMMAND_ISSUER_KINDS);
export type FleetCommandIssuerKind = (typeof FLEET_COMMAND_ISSUER_KINDS)[number];

export const FLEET_COMMAND_OUTCOMES = [
  'accepted',
  'started',
  'already-running',
  'stopped',
  'already-stopped',
  'synced',
  'dirty',
  'not-fast-forwardable',
  'cleaned',
  'restarting',
  'unknown-profile',
  'refused',
  'failed',
  'timed-out',
  'ok',
  'no-pane',
  'log-absent',
  'reset',
  'uploaded',
  'handed-over',
] as const;
export const FleetCommandOutcomeSchema = z.enum(FLEET_COMMAND_OUTCOMES);
export type FleetCommandOutcome = (typeof FLEET_COMMAND_OUTCOMES)[number];

/**
 * The steps a host reports between taking a command and settling it (OPS-57),
 * each posted the moment it happens: `received` when the host has the command
 * in hand, `running` when its verb starts. `sent` is the backend's own step
 * (the tick or take that handed it out) and `settled` is the outcome, so
 * neither is a step a host posts.
 */
export const FLEET_COMMAND_PROGRESS_STEPS = ['received', 'running'] as const;
export const FleetCommandProgressStepSchema = z.enum(FLEET_COMMAND_PROGRESS_STEPS);
export type FleetCommandProgressStep = (typeof FLEET_COMMAND_PROGRESS_STEPS)[number];

/**
 * The three streams an agent writes to (IMM-910.6, ADR 0047) — selectable,
 * never merged.
 *
 * `build` is a spawned engine's stdout, so it is empty for exactly as long as
 * nothing is building; `daemon` is the supervisor's own decisions, which is the
 * stream that answers "why is this agent idle" (IMM-910.10); `pane` is the tmux
 * capture, and exists only where the daemon runs under tmux.
 */
export const FLEET_LOG_STREAMS = ['build', 'daemon', 'pane'] as const;
export const FleetLogStreamSchema = z.enum(FLEET_LOG_STREAMS);
export type FleetLogStream = (typeof FLEET_LOG_STREAMS)[number];

/**
 * The UI offers exactly three choices, so depth is an enum of three literals
 * rather than a bounded integer — a number would let a caller compose a value
 * the verb list's whole discipline exists to avoid.
 */
export const FLEET_LOG_TAIL_DEPTHS = [10, 50, 100] as const;
export type FleetLogTailDepth = (typeof FLEET_LOG_TAIL_DEPTHS)[number];

/** Both ends agree through this constant; the agent flags `truncated` at it. */
export const FLEET_LOG_TAIL_MAX_BYTES = 64 * 1024;

const FleetProfileSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z][a-z0-9-]*$/);

export const FLEET_COMMAND_EVENT_PREFIX = 'fleet-command ';
export const FLEET_COMMAND_OUTCOME_EVENT_PREFIX = 'fleet-command-outcome ';

/** Every host verb but `stop-all`, which alone carries `finish`. */
const FLEET_PLAIN_HOST_COMMAND_VERBS = FLEET_HOST_COMMAND_VERBS.filter(
  (verb): verb is Exclude<FleetHostCommandVerb, 'stop-all'> => verb !== 'stop-all',
) as [Exclude<FleetHostCommandVerb, 'stop-all'>, ...Exclude<FleetHostCommandVerb, 'stop-all'>[]];

/** No `profile` key at all — a host verb that arrived with one is a 400, not a field to ignore. */
const FleetHostCommandEventSchema = z
  .object({
    v: z.literal(1),
    commandId: z.uuid(),
    machineId: z.uuid(),
    verb: z.enum(FLEET_PLAIN_HOST_COMMAND_VERBS),
    issuedAt: z.iso.datetime(),
  })
  .strict();

/** `stop-all` (fleet-agent ADR 0013): `finish` lets every round in hand end first. */
const FleetStopAllCommandEventSchema = z
  .object({
    v: z.literal(1),
    commandId: z.uuid(),
    machineId: z.uuid(),
    verb: z.literal('stop-all'),
    finish: z.boolean().optional(),
    issuedAt: z.iso.datetime(),
  })
  .strict();

/**
 * `start` / `restart` / `stop` (fleet-agent ADR 0013): one lane of the host, narrowed to one project or not. The
 * host keeps a stop until a `start`, across reboots and updates; `finish` (stop only) waits for the round in hand.
 */
const FleetLaneCommandEventSchema = z
  .object({
    v: z.literal(1),
    commandId: z.uuid(),
    machineId: z.uuid(),
    verb: z.enum(['start', 'restart']),
    profile: FleetProfileSchema,
    project: FleetProjectNameSchema.optional(),
    issuedAt: z.iso.datetime(),
  })
  .strict();

const FleetLaneStopCommandEventSchema = z
  .object({
    v: z.literal(1),
    commandId: z.uuid(),
    machineId: z.uuid(),
    verb: z.literal('stop'),
    profile: FleetProfileSchema,
    project: FleetProjectNameSchema.optional(),
    finish: z.boolean().optional(),
    issuedAt: z.iso.datetime(),
  })
  .strict();

const FleetLogCommandEventSchema = z.object({
  v: z.literal(1),
  commandId: z.uuid(),
  machineId: z.uuid(),
  verb: z.literal('logs'),
  profile: FleetProfileSchema,
  stream: FleetLogStreamSchema,
  lines: z.union([z.literal(10), z.literal(50), z.literal(100)]),
  issuedAt: z.iso.datetime(),
});

/** `uploadUrl` is the ONE capability that never travels stored — minted at publish, alongside the command. */
const FleetLogDownloadCommandEventSchema = z.object({
  v: z.literal(1),
  commandId: z.uuid(),
  machineId: z.uuid(),
  verb: z.literal('logs-download'),
  profile: FleetProfileSchema,
  stream: FleetLogStreamSchema,
  uploadUrl: z.url(),
  issuedAt: z.iso.datetime(),
});

const FleetLogResetCommandEventSchema = z.object({
  v: z.literal(1),
  commandId: z.uuid(),
  machineId: z.uuid(),
  verb: z.literal('logs-reset'),
  profile: FleetProfileSchema,
  issuedAt: z.iso.datetime(),
});

const FleetHandoverCommandEventSchema = z
  .object({
    v: z.literal(1),
    commandId: z.uuid(),
    machineId: z.uuid(),
    verb: z.literal('handover'),
    ticket: FleetTicketSchema,
    issuedAt: z.iso.datetime(),
  })
  .strict();

const FleetE2eUnlockCommandEventSchema = z
  .object({
    v: z.literal(1),
    commandId: z.uuid(),
    machineId: z.uuid(),
    verb: z.literal('e2e-unlock'),
    project: FleetProjectNameSchema.optional(),
    worker: FleetWorkerSchema.optional(),
    issuedAt: z.iso.datetime(),
  })
  .strict();

const FleetRunCommandEventSchema = z
  .object({
    v: z.literal(1),
    commandId: z.uuid(),
    machineId: z.uuid(),
    verb: z.literal('run-command'),
    project: FleetProjectNameSchema,
    argv: FleetRunCommandArgvSchema,
    issuedAt: z.iso.datetime(),
  })
  .strict();

/** `system-preview`/`system-prune` (ADR 0018): the host's disk report or its prune; `project` narrows the worktrees. */
const FleetSystemCommandEventSchema = z
  .object({
    v: z.literal(1),
    commandId: z.uuid(),
    machineId: z.uuid(),
    verb: z.enum(FLEET_SYSTEM_COMMAND_VERBS),
    project: FleetProjectNameSchema.optional(),
    issuedAt: z.iso.datetime(),
  })
  .strict();

/** `agent` (ADR 0070): the host's own verb, carried as the operator sent it. */
const FleetAgentCommandEventSchema = z
  .object({
    v: z.literal(1),
    commandId: z.uuid(),
    machineId: z.uuid(),
    verb: z.literal('agent'),
    action: FleetAgentActionSchema,
    args: FleetAgentArgsSchema,
    issuedAt: z.iso.datetime(),
  })
  .strict();

/**
 * `engine`: no `lane` sets the host default, a `lane` its override (narrowed by `project`); `engine: null` unsets
 * it. Models are optional — absent, the adapter's own mapping.
 */
const FleetEngineCommandFields = {
  engine: FleetEngineNameSchema.nullable(),
  lane: FleetLaneNameSchema.optional(),
  project: FleetProjectNameSchema.optional(),
  planModel: FleetModelIdSchema.optional(),
  implementModel: FleetModelIdSchema.optional(),
};

const FleetEngineCommandEventSchema = z
  .object({
    v: z.literal(1),
    commandId: z.uuid(),
    machineId: z.uuid(),
    verb: z.literal('engine'),
    ...FleetEngineCommandFields,
    issuedAt: z.iso.datetime(),
  })
  .strict();

export const FleetCommandEventSchema = z.discriminatedUnion('verb', [
  FleetHandoverCommandEventSchema,
  FleetE2eUnlockCommandEventSchema,
  FleetRunCommandEventSchema,
  FleetSystemCommandEventSchema,
  FleetAgentCommandEventSchema,
  FleetEngineCommandEventSchema,
  FleetHostCommandEventSchema,
  FleetStopAllCommandEventSchema,
  FleetLaneCommandEventSchema,
  FleetLaneStopCommandEventSchema,
  FleetLogCommandEventSchema,
  FleetLogDownloadCommandEventSchema,
  FleetLogResetCommandEventSchema,
]);
export type FleetCommandEvent = z.infer<typeof FleetCommandEventSchema>;

/** What a settled `logs` command carried back — newest first, possibly empty (BL-049: empty is not absent). */
export const FleetLogTailPayloadSchema = z.object({
  kind: z.literal('log-tail'),
  stream: FleetLogStreamSchema,
  lines: z.array(z.string()),
  truncated: z.boolean(),
  capturedAt: z.iso.datetime(),
});
export type FleetLogTailPayload = z.infer<typeof FleetLogTailPayloadSchema>;

/** What a settled `logs-download` command carried back — the object key the backend derives and compares. */
export const FleetLogObjectPayloadSchema = z.object({
  kind: z.literal('log-object'),
  stream: FleetLogStreamSchema,
  objectKey: z.string(),
  sizeBytes: z.number().int().nonnegative(),
});
export type FleetLogObjectPayload = z.infer<typeof FleetLogObjectPayloadSchema>;

/**
 * What a settled `handover` carried back (OPS-29): the snapshot each of the
 * ticket's worktrees was pushed as — verified on origin before the worktree
 * was removed — and the worktrees removed. A worktree with nothing unpushed or
 * dirty is removed with no snapshot.
 */
export const FleetHandoverPayloadSchema = z.object({
  kind: z.literal('handover'),
  ticket: FleetTicketSchema,
  snapshots: z.array(
    z.object({
      worktree: z.string().min(1).max(200),
      ref: z.string().min(1).max(300),
      commit: z.string().regex(/^[0-9a-f]{40}$/),
    }),
  ),
  removed: z.array(z.string().min(1).max(200)),
});
export type FleetHandoverPayload = z.infer<typeof FleetHandoverPayloadSchema>;

/** What a settled `e2e-unlock` carried back (IMM-1153): whose stack, and who held its lock until then. */
export const FleetE2eUnlockPayloadSchema = z.object({
  kind: z.literal('e2e-unlock'),
  worker: z.string().min(1).max(128),
  holder: z
    .object({
      owner: z.string().max(128),
      host: z.string().max(255),
      worktree: z.string().max(500),
      since: z.string().max(64),
    })
    .nullable(),
});
export type FleetE2eUnlockPayload = z.infer<typeof FleetE2eUnlockPayloadSchema>;

/** What a settled `run-command` carried back (ADR 0016): its exit code (`null`: it never exited) and its output's tail. */
export const FleetRunCommandPayloadSchema = z.object({
  kind: z.literal('run-command'),
  exitCode: z.number().int().nullable(),
  output: z.string().max(RUN_COMMAND_OUTPUT_MAX_CHARS),
  truncated: z.boolean(),
});
export type FleetRunCommandPayload = z.infer<typeof FleetRunCommandPayloadSchema>;

/** What a settled `system-preview`/`system-prune` carried back (ADR 0018): the tail of the report it printed. */
export const FleetSystemPayloadSchema = z.object({
  kind: z.literal('system'),
  output: z.string().max(SYSTEM_OUTPUT_MAX_CHARS),
  truncated: z.boolean(),
});
export type FleetSystemPayload = z.infer<typeof FleetSystemPayloadSchema>;

/** What a settled `agent` command carried back (ADR 0070): the tail of the output the host's action printed. */
export const FleetAgentPayloadSchema = z.object({
  kind: z.literal('agent'),
  output: z.string().max(AGENT_OUTPUT_MAX_CHARS),
  truncated: z.boolean(),
});
export type FleetAgentPayload = z.infer<typeof FleetAgentPayloadSchema>;

/** Always an object, never a bare scalar (BL-129 — the jsonb column this backs must stay object-only). */
export const FleetCommandPayloadSchema = z.discriminatedUnion('kind', [
  FleetLogTailPayloadSchema,
  FleetLogObjectPayloadSchema,
  FleetHandoverPayloadSchema,
  FleetE2eUnlockPayloadSchema,
  FleetRunCommandPayloadSchema,
  FleetSystemPayloadSchema,
  FleetAgentPayloadSchema,
]);
export type FleetCommandPayload = z.infer<typeof FleetCommandPayloadSchema>;

export const FleetCommandOutcomeEventSchema = z.object({
  v: z.literal(1),
  commandId: z.uuid(),
  machineId: z.uuid(),
  verb: FleetCommandVerbSchema,
  /** `null` for a host verb — it settles for the machine, not for a lane. */
  profile: z.string().min(1).max(64).nullable().default(null),
  outcome: FleetCommandOutcomeSchema,
  detail: z.string().max(500).nullable().default(null),
  payload: FleetCommandPayloadSchema.nullable().default(null),
  settledAt: z.iso.datetime(),
});
export type FleetCommandOutcomeEvent = z.infer<typeof FleetCommandOutcomeEventSchema>;

/**
 * A host verb may name a `project` (fleet-agent's CLI can send one), but it acts on the whole host whatever it
 * names: the backend neither stores nor forwards it.
 */
const FleetHostCommandBodySchema = z
  .object({
    verb: z.enum(FLEET_PLAIN_HOST_COMMAND_VERBS), // no `profile` key — the verb acts on the whole checkout
    project: FleetProjectNameSchema.optional(),
  })
  .strict();

const FleetStopAllCommandBodySchema = z
  .object({
    verb: z.literal('stop-all'),
    project: FleetProjectNameSchema.optional(),
    finish: z.boolean().optional(), // absent: at the next step boundary
  })
  .strict();

const FleetLaneCommandBodySchema = z
  .object({
    verb: z.enum(['start', 'restart']),
    profile: FleetProfileSchema, // the lane
    project: FleetProjectNameSchema.optional(), // absent: every project the host serves that declares it
  })
  .strict();

const FleetLaneStopCommandBodySchema = z
  .object({
    verb: z.literal('stop'),
    profile: FleetProfileSchema,
    project: FleetProjectNameSchema.optional(),
    finish: z.boolean().optional(),
  })
  .strict();

const FleetLogCommandBodySchema = z
  .object({
    verb: z.literal('logs'),
    profile: FleetProfileSchema,
    stream: FleetLogStreamSchema,
    // Spelled out rather than mapped over FLEET_LOG_TAIL_DEPTHS: z.union needs a
    // tuple of at least two members, and a mapped readonly array does not
    // satisfy that type.
    lines: z.union([z.literal(10), z.literal(50), z.literal(100)]),
  })
  .strict();

const FleetLogDownloadCommandBodySchema = z
  .object({
    verb: z.literal('logs-download'),
    profile: FleetProfileSchema,
    stream: FleetLogStreamSchema,
  })
  .strict();

const FleetLogResetCommandBodySchema = z
  .object({
    verb: z.literal('logs-reset'),
    profile: FleetProfileSchema, // no `stream` key — `stream: 'pane'` is a 400
  })
  .strict();

const FleetHandoverCommandBodySchema = z
  .object({
    verb: z.literal('handover'),
    ticket: FleetTicketSchema, // no `profile` key — the verb names a ticket's worktrees, not a lane
  })
  .strict();

const FleetE2eUnlockCommandBodySchema = z
  .object({
    verb: z.literal('e2e-unlock'),
    project: FleetProjectNameSchema.optional(), // absent: every project the host serves that has a stack
    worker: FleetWorkerSchema.optional(), // absent: the host's manual stack
  })
  .strict();

const FleetRunCommandBodySchema = z
  .object({
    verb: z.literal('run-command'),
    project: FleetProjectNameSchema, // one checkout, always named: a command is never fanned out to every project
    argv: FleetRunCommandArgvSchema,
  })
  .strict();

const FleetSystemCommandBodySchema = z
  .object({
    verb: z.enum(FLEET_SYSTEM_COMMAND_VERBS),
    project: FleetProjectNameSchema.optional(), // absent: every served project's worktrees; Docker and pnpm either way
  })
  .strict();

export const FleetAgentCommandBodySchema = z
  .object({
    verb: z.literal('agent'),
    action: FleetAgentActionSchema,
    args: FleetAgentArgsSchema,
  })
  .strict();

const FleetEngineCommandBodySchema = z
  .object({
    verb: z.literal('engine'),
    ...FleetEngineCommandFields,
  })
  .strict();

/**
 * `.strict()` on every member is load-bearing — an undeclared field is refused,
 * never silently stripped (the security argument rests on the verb enum and
 * the bounded parameters each member declares).
 */
export const FleetCommandBodySchema = z
  .discriminatedUnion('verb', [
    FleetHostCommandBodySchema,
    FleetStopAllCommandBodySchema,
    FleetLaneCommandBodySchema,
    FleetLaneStopCommandBodySchema,
    FleetLogCommandBodySchema,
    FleetLogDownloadCommandBodySchema,
    FleetLogResetCommandBodySchema,
    FleetHandoverCommandBodySchema,
    FleetE2eUnlockCommandBodySchema,
    FleetRunCommandBodySchema,
    FleetSystemCommandBodySchema,
    FleetAgentCommandBodySchema,
    FleetEngineCommandBodySchema,
  ])
  // An override names a lane; a project alone narrows nothing, and unsetting takes no models.
  .refine((body) => body.verb !== 'engine' || body.project === undefined || body.lane !== undefined, {
    message: 'project requires lane',
    path: ['project'],
  })
  .refine(
    (body) =>
      body.verb !== 'engine' ||
      body.engine !== null ||
      (body.planModel === undefined && body.implementModel === undefined),
    { message: 'unsetting takes no models', path: ['engine'] },
  );
export type FleetCommandBody = z.infer<typeof FleetCommandBodySchema>;

export const FleetCommandViewSchema = z.object({
  id: z.uuid(),
  machineId: z.uuid(),
  verb: FleetCommandVerbSchema,
  /** `null` for a host verb (`sync-branch`, `stop-all`, …) — it names no lane. */
  profile: z.string().nullable(),
  /** `admin` for an operator's press, `machine` for a peer's `fleet-agent agent fleet` (IMM-939). */
  requestedByKind: FleetCommandIssuerKindSchema,
  requestedByName: z.string().nullable(),
  requestedAt: z.iso.datetime(),
  outcome: FleetCommandOutcomeSchema,
  detail: z.string().nullable(),
  stream: FleetLogStreamSchema.nullable(),
  lines: z.union([z.literal(10), z.literal(50), z.literal(100)]).nullable(),
  /** `null` for every verb but `handover` — the ticket whose worktrees it bundled. */
  ticket: z.string().nullable(),
  /** Only on `stop` / `stop-all` (fleet-agent ADR 0013): it waited for the round in hand; absent from an older backend. */
  finish: z.boolean().nullish(),
  /**
   * Only on `engine` — the engine it set (`null` unset), the lane (`null`: the default), the one project that
   * lane override was narrowed to (`null`: every project) and the models it named. `project` is also the one
   * project a `start` / `stop` / `restart` / `e2e-unlock` / `system-*` was narrowed to (`null`: every project).
   */
  engine: z.string().nullish(),
  lane: z.string().nullish(),
  project: z.string().nullish(),
  planModel: z.string().nullish(),
  implementModel: z.string().nullish(),
  /** Only on `steer` (OPS-55) — the job it was sent to and the message; absent from a backend that predates it. */
  jobId: z.uuid().nullish(),
  message: z.string().nullish(),
  /** Only on `e2e-unlock` — whose stack it unlocked (`null`: the host's manual stack); absent from an older backend. */
  worker: z.string().nullish(),
  /** Only on `run-command` — the words it ran; absent from an older backend. */
  argv: z.array(z.string()).nullish(),
  /** Only on `agent` (ADR 0070) — the host verb it named and its args; absent from an older backend. */
  action: z.string().nullish(),
  args: z.record(z.string(), z.unknown()).nullish(),
  payload: FleetCommandPayloadSchema.nullable(),
  /** Minted per read for a settled `logs-download`; null otherwise. Never stored. */
  downloadUrl: z.url().nullable(),
  downloadExpiresInSeconds: z.number().int().positive().nullable(),
  /**
   * The steps between issue and settle (OPS-57): the backend handed it out
   * (`sentAt`), the host had it in hand (`receivedAt`), its verb started
   * (`startedAt`). Optional so a page reads a backend that predates them.
   */
  sentAt: z.iso.datetime().nullish(),
  receivedAt: z.iso.datetime().nullish(),
  startedAt: z.iso.datetime().nullish(),
  settledAt: z.iso.datetime().nullable(),
});
export type FleetCommandView = z.infer<typeof FleetCommandViewSchema>;

export const FleetCommandAcceptedResponseSchema = z.object({
  commandId: z.uuid(),
  outcome: z.literal('accepted'),
});
export type FleetCommandAcceptedResponse = z.infer<typeof FleetCommandAcceptedResponseSchema>;

export const ListFleetCommandsResponseSchema = z.object({ data: z.array(FleetCommandViewSchema) });
export type ListFleetCommandsResponse = z.infer<typeof ListFleetCommandsResponseSchema>;

export const FleetCommandPathParamsSchema = z.object({ machineId: z.uuid(), commandId: z.uuid() });

/**
 * The WORKER's outcome route addresses a command and nothing else.
 *
 * It cannot borrow {@link FleetCommandPathParamsSchema}: that one belongs to
 * the operator route, whose path carries `:machineId` too. Validating the
 * worker's path against it demands a parameter the URL has no segment for, so
 * every outcome a worker posts is refused 400 before the handler runs — and
 * the command sits at `accepted` for ever, looking taken but never answered.
 */
export const FleetCommandOutcomePathParamsSchema = z.object({ commandId: z.uuid() });
export type FleetCommandOutcomePathParams = z.infer<typeof FleetCommandOutcomePathParamsSchema>;
export type FleetCommandPathParams = z.infer<typeof FleetCommandPathParamsSchema>;

export const FleetCommandsPathParamsSchema = z.object({ machineId: z.uuid() });
export type FleetCommandsPathParams = z.infer<typeof FleetCommandsPathParamsSchema>;

/**
 * The ONE place this string is built (BL-126) — both ends compute the same
 * key, and the backend presigns only this DERIVED value, never one a machine
 * reports back to it (a machine-supplied key would let a host name any object
 * in the bucket and have the backend mint an operator-readable GET for it).
 */
export function fleetLogObjectKey(input: {
  readonly machineId: string;
  readonly commandId: string;
  readonly profile: string;
  readonly stream: FleetLogStream;
}): string {
  return `fleet-logs/${input.machineId}/${input.commandId}/${input.profile}.${input.stream}.log`;
}

/** What a worker posts back after running a verb (IMM-914.4). */
export const RecordFleetCommandOutcomeBodySchema = z.object({
  outcome: FleetCommandOutcomeSchema,
  detail: z.string().max(500).nullish(),
  payload: FleetCommandPayloadSchema.nullish(),
});

export type RecordFleetCommandOutcomeBody = z.infer<typeof RecordFleetCommandOutcomeBodySchema>;

export const RecordFleetCommandOutcomeResponseSchema = z.object({
  commandId: z.uuid(),
  settledAt: z.iso.datetime(),
});

export type RecordFleetCommandOutcomeResponse = z.infer<typeof RecordFleetCommandOutcomeResponseSchema>;

/**
 * A host's step on a command it holds (OPS-57), posted the moment it happens
 * rather than with the next report. Idempotent per step: the first `at`
 * recorded stands, and a step for a settled command changes nothing.
 */
export const RecordFleetCommandProgressBodySchema = z
  .object({
    step: FleetCommandProgressStepSchema,
    at: z.iso.datetime(),
  })
  .strict();

export type RecordFleetCommandProgressBody = z.infer<typeof RecordFleetCommandProgressBodySchema>;

export const RecordFleetCommandProgressResponseSchema = z.object({
  commandId: z.uuid(),
  step: FleetCommandProgressStepSchema,
  at: z.iso.datetime(),
});

export type RecordFleetCommandProgressResponse = z.infer<typeof RecordFleetCommandProgressResponseSchema>;
